import styled from 'styled-components';
import { dimensions, colors, typography } from 'modules/common/styles';
import { rgba } from 'modules/common/styles/color';
import { ActionButtons } from 'modules/common/styles/stage';
import { SortItem } from 'modules/common/styles/sort';

const coreSpace = `${dimensions.coreSpacing}px`;
const unitSpace = `${dimensions.unitSpacing}px`;

const DataWrapperClass = styled.div`
  section.sc-iELTvK.dgWnOn {
    background: #fff;
  }
  section.sc-jtRfpW.daxgFZ {
    margin-right: 20px;
  }
  section.sc-iELTvK.dstvIj {
    background: #fff;
    margin-left: 20px;
  }
`;

const ContentBox = styled.div`
  padding: ${dimensions.coreSpacing}px;
`;

const ModuleBox = styled.div`
  padding: ${coreSpace} 0 0 ${coreSpace};
`;

const LogoContainer = styled.div`
  color: ${colors.colorWhite};
  line-height: 56px;
  text-align: center;
  border-radius: 28px;
  width: 56px;
  height: 56px;
  cursor: pointer;
  box-shadow: 0 0 ${unitSpace} 0 ${rgba(colors.colorBlack, 0.2)};
  background-image: url('/images/erxes.png');
  background-color: ${colors.colorPrimary};
  background-position: center;
  background-size: 20px;
  background-repeat: no-repeat;
  margin-top: ${unitSpace};
  position: relative;
  float: right;

  span {
    position: absolute;
    width: ${coreSpace};
    height: ${coreSpace};
    background: ${colors.colorCoreRed};
    display: block;
    right: -2px;
    top: -5px;
    color: ${colors.colorWhite};
    border-radius: ${unitSpace};
    text-align: center;
    line-height: ${coreSpace};
    font-size: ${unitSpace};
  }

  input[type='file'] {
    display: none;
  }

  label {
    display: block;
    margin: 0;
    visibility: hidden;
    border-radius: 50%;
  }

  &:hover label {
    visibility: visible;
    cursor: pointer;
  }
`;

const ColorPick = styled.div`
  border-radius: 4px;
  display: ${props => (props.full ? 'block' : 'inline-block')};
  padding: 5px;
  border: 1px solid ${colors.borderPrimary};
  cursor: pointer;
`;

const LineButton = styled.a`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ColorPicker = styled.div`
  width: ${props => (props.full ? '100%' : '80px')};
  height: 15px;
`;

const WidgetApperance = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 100%;
  padding: ${coreSpace};
`;

const WidgetBackgrounds = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const SubHeading = styled.h4`
  text-transform: uppercase;
  font-weight: ${typography.fontWeightMedium};
  border-bottom: 1px dotted ${colors.colorShadowGray};
  padding-bottom: ${coreSpace};
  font-size: ${typography.fontSizeHeading8}px;
  margin: 0 0 ${coreSpace};
`;

const MarkdownWrapper = styled.div`
  position: relative;
  background: ${colors.colorWhite};
  border: 1px solid ${colors.colorShadowGray};
  border-radius: 2px;

  > div {
    background: none;
  }

  button {
    position: absolute;
    right: ${coreSpace};
    top: ${coreSpace};
  }

  pre {
    border: none;
    background: none;
    margin: 0;
  }
`;

const InlineItems = styled.div`
  display: flex;
  margin-bottom: ${unitSpace};
  align-items: center;
  justify-content: space-between;

  > div {
    margin-right: ${unitSpace};
  }
`;

const SubItem = styled.div`
  margin-bottom: ${coreSpace};

  img {
    background-color: ${colors.colorLightGray};
    max-height: 100px;
    margin-right: 5px;
  }

  i:hover {
    cursor: pointer;
  }
`;

const BackgroundSelector = styled.div`
  border: 3px solid transparent;
  margin-right: 15px;
  border-radius: 4px;
  transition: border-color 0.3s;

  > div {
    width: 80px;
    height: 40px;
    margin: 5px;
    border: 1px solid ${colors.borderDarker};
    background-repeat: repeat;
    background-position: 0 0;
    background-size: 220%;

    &.background-1 {
      background-image: url('/images/patterns/bg-1.png');
    }

    &.background-2 {
      background-image: url('/images/patterns/bg-2.png');
    }

    &.background-3 {
      background-image: url('/images/patterns/bg-3.png');
    }

    &.background-4 {
      background-image: url('/images/patterns/bg-4.png');
    }

    &.background-5 {
      background: #faf9fb;
    }
  }

  &.selected {
    border-color: ${colors.borderDarker};
  }

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    cursor: pointer;
  }
`;

const PipelineRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  border-top: 1px solid rgb(238, 238, 238);
  padding: 10px 20px;
`;

const StageItemContainer = styled(PipelineRowContainer)`
  background-color: ${colors.colorWhite};
  padding: 0;
  border-top: 0;
  align-items: center;
  > *:not(button) {
    margin-right: 10px;
  }
  button {
    padding: 3px;
    font-size: 16px;
  }
  button:hover {
    color: ${colors.colorCoreRed};
  }
`;

const StageList = styled.div`
  > button {
    margin-top: 10px;
  }

  ${SortItem} {
    padding: 5px 15px;
  }
`;

const SidebarListItem = styled.li`
  position: relative;
  border-bottom: 1px solid ${colors.borderPrimary};
  background: ${props => props.isActive && colors.bgActive};
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;

  a {
    white-space: normal;
    flex: 1;
    padding: 10px 0 10px 20px;

    &:hover {
      background: none;
    }

    &:focus {
      color: inherit;
      text-decoration: none;
    }
  }

  &:last-child {
    border: none;
  }

  &:hover {
    cursor: pointer;
    background: ${props => !props.isActive && colors.bgLight};

    ${ActionButtons} {
      transform: translate(0px);
    }
  }
`;
const CenterFlexRow = styled.div`
  display: flex;
  align-items: center;
  //margin-top: 35%;
  > label, h6 {
    line-height: 49px;
    font-size: 36px;
    font-weight: 700;
    margin-top: 12px;
    color: #2B3C93;
    letter-spacing: 0.1em;
    margin: 0;
    height: auto;
    display: inline-flex;
    text-transform: uppercase;
  }
  @media (max-width: 767px) {
    > label, h6 {
      font-size: 22px !important;
    }
  }
`;

const BackButton = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 35px;
  line-height: 35px;
  background: rgba(0, 0, 0, 0.12);
  text-align: center;
  margin-right: ${dimensions.unitSpacing}px;
  color: ${colors.textPrimary};
  transition: all ease 0.3s;
  &:hover {
    background: rgba(0, 0, 0, 0.18);
  }
`;

const Title = styled(CenterFlexRow)`
  transition: all ease 0.3s;
  padding: 0 ${dimensions.unitSpacing}px;
  border-radius: 4px;
  input {
    font-size: 14px;
    height: auto;
    padding: 5px 20px;
    border-radius: 32px;
    border: 1px solid #e2e2e2;
  }
  i {
    visibility: hidden;
  }
  &:hover {
    background: ${colors.bgActive};
    i {
      visibility: visible;
    }
  }
`;

export {
  ContentBox,
  ModuleBox,
  SubHeading,
  MarkdownWrapper,
  InlineItems,
  SubItem,
  WidgetApperance,
  WidgetBackgrounds,
  BackgroundSelector,
  ColorPick,
  ColorPicker,
  LogoContainer,
  SidebarListItem,
  StageItemContainer,
  ActionButtons,
  StageList,
  LineButton,
  DataWrapperClass,
  CenterFlexRow,
  BackButton,
  Title
};



// WEBPACK FOOTER //
// src/modules/screens/settings/styles.js